import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { computed, effect, type ReadonlySignal } from '@preact/signals-core';
import type { PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';

import type { Cell } from '../view-manager/cell.js';
import type { CellRenderProps, DataViewCellLifeCycle } from './manager.js';

export abstract class BaseCellRenderer<
  RawValue = unknown,
  JsonValue = unknown,
  Data extends Record<string, unknown> = Record<string, unknown>,
>
  extends SignalWatcher(WithDisposable(ShadowlessElement))
  implements DataViewCellLifeCycle, CellRenderProps<Data, RawValue, JsonValue>
{
  private editingPoint: { clientX: number; clientY: number } | undefined;

  get expose() {
    return this;
  }

  @property({ attribute: false })
  accessor cell!: Cell<RawValue, JsonValue, Data>;

  readonly$ = computed(() => {
    return this.cell.property.valueReadonly$.value;
  });

  value$ = computed(() => {
    return this.cell.value$.value;
  });

  get property() {
    return this.cell.property;
  }

  get readonly() {
    return this.readonly$.value;
  }

  get row() {
    return this.cell.row;
  }

  get value() {
    return this.value$.value;
  }

  get view() {
    return this.cell.view;
  }

  beforeEnterEditMode(): boolean {
    return true;
  }

  blurCell() {
    return true;
  }

  type: string | undefined;

  protected override shouldUpdate(_changedProperties: PropertyValues): boolean {
    return this.cell.property.type$.value === this.type;
  }

  override connectedCallback() {
    super.connectedCallback();
    this.type = this.cell.property.type$.value;
    this.dataset.testid = this.type;
    this.style.width = '100%';
    this._disposables.addFromEvent(this, 'click', e => {
      if (this.isEditing$.value) {
        e.stopPropagation();
      }
    });

    this._disposables.addFromEvent(this, 'copy', e => {
      if (!this.isEditing$.value) return;
      e.stopPropagation();
      this.onCopy(e);
    });

    this._disposables.addFromEvent(this, 'cut', e => {
      if (!this.isEditing$.value) return;
      e.stopPropagation();
      this.onCut(e);
    });

    this._disposables.addFromEvent(this, 'paste', e => {
      if (!this.isEditing$.value) return;
      e.stopPropagation();
      this.onPaste(e);
    });

    // Single canonical bridge from the editing signal to the cell's
    // `afterEnterEditingMode` / `beforeExitEditingMode` hooks. Putting
    // it here means every view (table, virtual-table, kanban, detail,
    // gantt) gets the hooks fired by simply toggling `isEditing$` —
    // no more per-controller or per-container plumbing. Views that
    // previously invoked the hooks by hand (table selection
    // controllers, kanban cell component) drop those calls; the
    // signal toggle alone is the source of truth.
    let enterFrame: number | undefined;
    let lifecycleEntered = false;
    let lastEditing = this.isEditing$?.value ?? false;
    const cancelPendingEnter = () => {
      if (enterFrame === undefined) return;
      cancelAnimationFrame(enterFrame);
      enterFrame = undefined;
    };
    const ownsInteractiveSurface = () => {
      const rect = this.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;
      const topmostRenderer = this.ownerDocument
        .elementsFromPoint(
          rect.left + rect.width / 2,
          rect.top + rect.height / 2
        )
        .find(element => element instanceof BaseCellRenderer);
      return topmostRenderer === this;
    };
    this._disposables.add(cancelPendingEnter);
    this._disposables.add(
      effect(() => {
        const editing = this.isEditing$?.value ?? false;
        if (editing === lastEditing) return;
        lastEditing = editing;
        cancelPendingEnter();
        if (editing) {
          // Defer to the next frame: the signal flip typically races
          // with the DOM update that mounts the editing UI (e.g. the
          // popover target needs this element connected). `focusCell`
          // is still the controller's responsibility — it stays in
          // the controller's own raf block.
          enterFrame = requestAnimationFrame(() => {
            enterFrame = undefined;
            // A data view can render the same logical cell in more than one
            // surface (for example Edgeless behind a fullscreen database).
            // Only the visible renderer may own focus and editing hooks.
            if (
              this.isConnected &&
              this.isEditing$?.value &&
              ownsInteractiveSurface()
            ) {
              lifecycleEntered = true;
              this.afterEnterEditingMode();
            }
          });
        } else if (lifecycleEntered) {
          lifecycleEntered = false;
          this.beforeExitEditingMode();
        }
      })
    );
  }

  focusCell() {
    return true;
  }

  setEditingPoint(point: { clientX: number; clientY: number }): void {
    this.editingPoint = point;
  }

  protected takeEditingPoint() {
    const point = this.editingPoint;
    this.editingPoint = undefined;
    return point;
  }

  protected focusInputAtEditingPoint(input: HTMLInputElement): void {
    const point = this.takeEditingPoint();
    input.focus();
    if (!point) {
      const end = input.value.length;
      input.setSelectionRange(end, end);
      return;
    }

    const style = getComputedStyle(input);
    const rect = input.getBoundingClientRect();
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      const end = input.value.length;
      input.setSelectionRange(end, end);
      return;
    }

    context.font = style.font;
    const letterSpacing = Number.parseFloat(style.letterSpacing) || 0;
    const textWidth =
      context.measureText(input.value).width +
      Math.max(0, input.value.length - 1) * letterSpacing;
    const paddingLeft = Number.parseFloat(style.paddingLeft) || 0;
    const paddingRight = Number.parseFloat(style.paddingRight) || 0;
    const contentLeft = rect.left + paddingLeft;
    const contentRight = rect.right - paddingRight;
    const textLeft =
      style.textAlign === 'right'
        ? contentRight - textWidth
        : style.textAlign === 'center'
          ? contentLeft + (contentRight - contentLeft - textWidth) / 2
          : contentLeft;
    const targetX = point.clientX - textLeft;
    let caret = input.value.length;
    for (let index = 0; index < input.value.length; index++) {
      const before =
        context.measureText(input.value.slice(0, index)).width +
        index * letterSpacing;
      const after =
        context.measureText(input.value.slice(0, index + 1)).width +
        index * letterSpacing;
      if (targetX < (before + after) / 2) {
        caret = index;
        break;
      }
    }
    input.setSelectionRange(caret, caret);
  }

  forceUpdate(): void {
    this.requestUpdate();
  }

  valueSetImmediate(value: RawValue | undefined): void {
    this.cell.valueSet(value);
  }

  valueSetNextTick(value: RawValue | undefined) {
    requestAnimationFrame(() => {
      this.cell.valueSet(value);
    });
  }

  onCopy(_e: ClipboardEvent) {}

  onCut(_e: ClipboardEvent) {}

  afterEnterEditingMode(): void {
    // do nothing
  }

  beforeExitEditingMode() {
    // do nothing
  }

  onPaste(_e: ClipboardEvent) {}

  @property({ attribute: false })
  accessor isEditing$!: ReadonlySignal<boolean>;

  @property({ attribute: false })
  accessor selectCurrentCell!: (editing: boolean) => void;
}
